import * as React from 'react';
import { connect } from 'react-redux';
import './ProjectSettingsPanel.css'
import { PanelType, MessageBarType, MessageBar, PrimaryButton, DefaultButton, ChoiceGroup } from 'office-ui-fabric-react';
import { ProjectInfo, ProjectSettings, IWithProcess, ProjectSettingsUpdateData } from '../../../store/ProjectsListStore';
import { IInsightsData, PANEL_CUSTOM_WIDTH, ProgressCalculationType, ServerEntityType, TaskType } from '../../../entities/common';
import { Insights, PPMFeatures, Subscription, TenantState } from '../../../store/Tenant';
import { ApplicationState } from '../../../store';
import ExpandablePanel from '../../common/ExpandablePanel';
import { UserState } from '../../../store/User';
import Collapsible from '../../common/Collapsible';
import { PrimaryScheduleSection } from './PrimaryScheduleSection';
import { AutoCalculationSection } from './AutoCalculationSection';
import { ZeroDayMilestoneFsBehaviorSection } from './ZeroDayMilestoneFsBehaviorSection';
import { DirtyFormMessageBar } from '../../../components/common/DirtyFormMessageBar';
import TimeTrackingProjectSettingsSection from './TimeTrackingProjectSettingsSection';
import ProjectProcessSection from './ProjectProcessSection';
import { ResourcePlanSettingsSection } from './ResourcePlanSettingsSection';
import { notUndefined } from '../../utils/common';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { get } from '../../../fetch-interceptor';
import ScheduleCalculationSection from './ScheduleCalculationSection';
import CalendarSection from './CalendarSection';

type OwnProps = {
    entity: ProjectInfo;
    readonlyMode?: boolean;
    onDismiss: () => void;
    onSave: (changes: ProjectSettingsUpdateData) => void;
}

type StateProps = {
    user: UserState;
    tenant: TenantState;
    tenantInsights: Insights;
}

type Props = OwnProps & StateProps;

const saveButtonText = "Save";
const cancelButtonText = "Cancel";

type ProjectSettingsState = ProjectSettings & Partial<IWithProcess>;

const ProjectSettingsPanel = (props: Props) => {
    const [projectInsights, setProjectInsights] = React.useState(props.entity.insights);
    const [projectSettings, setProjectSettings] = React.useState<ProjectSettingsState>({
        ...props.entity.settings, 
        process: props.entity.process, 
        apiBasedApprovalUrl: props.entity.apiBasedApprovalUrl
    });
    const [showHasChangesWarning, setShowHasChangesWarning] = React.useState(false);
    const [pendingBehaviorChange, setPendingBehaviorChange] = React.useState<{
        affectedMilestones: { id: string; name: string }[];
        shouldRecalculateImmediately: boolean;
    }>();
    const [keepModeAction, setKeepModeAction] = React.useState<'recalculate' | 'manual'>('manual');
    const [hasFixedUnitsTasks, setHasFixedUnitsTasks] = React.useState(false);

    React.useEffect(() => {
        get<boolean>(`api/project/${props.entity.id}/settings/hasFixedUnitsTasks`)
            .then(setHasFixedUnitsTasks)
            .catch(() => { });
    }, [props.entity.id]);

    const readonly = !props.entity.isEditable || props.readonlyMode;

    const sectionsSettings: any[] = [
        {
            title: "Primary Schedule",
            key: "primary-schedule",
            iconName: "System",
            expanded: true,
            onRenderBody: () => <PrimaryScheduleSection
                readonly={readonly}
                sourceTypes={props.entity.sourceInfos.map(_ => _.type)}
                value={projectSettings.primarySchedule}
                onChange={(value) => setProjectSettings({ ...projectSettings, primarySchedule: value })} >
                <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                    All Schedule rollup fields, such as Schedule Start Date, Schedule Due Date, Schedule Progress %, etc.,
                    will be automatically recalculated to display values rolled up from the selected schedule.
                </MessageBar>
            </PrimaryScheduleSection>
        },
        {
            title: "Calculated automatically",
            key: "calculate-automatically",
            iconName: "Processing",
            onRenderBody: () => <AutoCalculationSection
                projectInsights={projectInsights}
                project={props.entity}
                tenantInsights={props.tenantInsights}
                calculateStartFinishDate={projectSettings.calculateStartFinishDate}
                readonly={readonly}
                onChangeInsights={(insights: Partial<IInsightsData>) => setProjectInsights({ ...projectInsights, ...insights })}
                onChangeCalculateStartFinishDate={(calculateStartFinishDate) => setProjectSettings({ ...projectSettings, calculateStartFinishDate: calculateStartFinishDate })}
            />
        },
        {
            title: "Schedule Calculation for PPM Express Tasks",
            key: "schedulecalculation",
            iconName: "Calendar",
            onRenderBody: () => <ScheduleCalculationSection
                value={projectSettings.useResourceCalendar}
                readonly={readonly}
                onChange={value => setProjectSettings({ ...projectSettings, useResourceCalendar: value })}
                progressCalculationType={projectSettings.progressCalculationType}
                onChangeProgressCalculationType={value => setProjectSettings({
                    ...projectSettings,
                    progressCalculationType: value,
                    ...(value === ProgressCalculationType.Effort ? undefined : { taskType: TaskType.Flexible, effortDriven: false })
                })}
                progressCalculationLocked={hasFixedUnitsTasks}
                isTaskAutoCalculationMode={projectSettings.isTaskAutoCalculationMode}
                onChangeMode={value => setProjectSettings({ ...projectSettings, isTaskAutoCalculationMode: value })}
                taskType={projectSettings.taskType}
                onChangeTaskType={value => setProjectSettings({ ...projectSettings, taskType: value })}
                effortDriven={projectSettings.effortDriven}
                onChangeEffortDriven={value => setProjectSettings({ ...projectSettings, effortDriven: value })} />
        },
        {
            title: "Behavior of 0-duration Tasks in PPM Express Tasks",
            key: "milestone-fs-behavior",
            iconName: "DateTimeMirrored",
            onRenderBody: () => <ZeroDayMilestoneFsBehaviorSection
                value={projectSettings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay}
                readonly={readonly}
                onChange={(value) => setProjectSettings({ ...projectSettings, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: value })} />
        },
        props.tenant.timeTracking.globalSettings.newTimeTrackingEnabled && Subscription.containsAny(props.tenant.subscription, [PPMFeatures.TimeTracking, PPMFeatures.TimeTrackingEnterprise]) 
            ? {
                title: "Time Tracking",
                key: "time-tracking",
                iconName: "Clock",
                onRenderBody: () => <TimeTrackingProjectSettingsSection
                    value={projectSettings.calculateCompletedWorkBasedOnReportedTime}
                    originalValue={props.entity.settings.calculateCompletedWorkBasedOnReportedTime}
                    readonly={readonly}
                    isPrivateProject={!!props.entity.isPrivate}
                    isGlobalSettingEnabled={props.tenant.timeTracking.globalSettings.calculateCompletedWorkBasedOnReportedTime}
                    allowTimeTracking={projectSettings.allowTimeTracking}
                    onChange={(value) => setProjectSettings({ ...projectSettings, ...value })} />
            }
            : undefined,
        props.tenant.resourcePlanningSettings?.resourcePlanningLevel === ServerEntityType.Project
            ? {
                title: "Resource Plan",
                key: "resource-plan",
                iconName: "PPMXSectionResourcePlan",
                onRenderBody: () => <ResourcePlanSettingsSection
                    value={props.entity.settings.estimatedChargeCalculationMode}
                    readonly={readonly}
                    onChange={(value) => setProjectSettings({ ...projectSettings, estimatedChargeCalculationMode: value })} />
            }
            : undefined,
        {
            title: "Project Process",
            key: "project-process",
            iconName: "CRMProcesses",
            onRenderBody: () => <ProjectProcessSection
                process={props.entity.process}
                value={{ process: projectSettings.process, apiBasedApprovalUrl: projectSettings.apiBasedApprovalUrl }}
                readonly={readonly}
                onChange={(value) => setProjectSettings({ ...projectSettings, ...value })} />
        },
        {
            title: "Calendar",
            key: "calendar",
            iconName: "Calendar",
            onRenderBody: () => <CalendarSection
                value={projectSettings.calendar}
                readonly={readonly}
                onChange={value => setProjectSettings({ ...projectSettings, calendar: value! })} />
        }
    ].filter(notUndefined);

    const isPrimaryScheduleChanged = () => props.entity.settings.primarySchedule !== projectSettings.primarySchedule;

    const isCalculationTypeChanged = () => props.entity.settings.progressCalculationType !== projectSettings.progressCalculationType;
    const isCalculationModeChanged = () => props.entity.settings.isTaskAutoCalculationMode !== projectSettings.isTaskAutoCalculationMode;
    const isMilestoneFsBehaviorChanged = () =>
        props.entity.settings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay !== projectSettings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay;
    const isCalculateStartFinishDateChanged = () => props.entity.settings.calculateStartFinishDate !== projectSettings.calculateStartFinishDate;
    
    const areProcessSettingsValid = () => projectSettings.process !== undefined;

    const areProcessSettingsChanged = () => props.entity.process.id !== projectSettings.process?.id
        || props.entity.apiBasedApprovalUrl !== projectSettings.apiBasedApprovalUrl

    const isCalculatedAutomaticalyChanged = () => {
        return props.entity.insights.keyDateStatusCalculationDisabled !== projectInsights.keyDateStatusCalculationDisabled
            || props.entity.insights.statusCalculationDisabled !== projectInsights.statusCalculationDisabled
            || props.entity.insights.taskStatusCalculationDisabled !== projectInsights.taskStatusCalculationDisabled
            || props.entity.insights.warningsCalculationDisabled !== projectInsights.warningsCalculationDisabled;
    }

    const isTimeTrackingSettingChanged = () => props.entity.settings.calculateCompletedWorkBasedOnReportedTime !== projectSettings.calculateCompletedWorkBasedOnReportedTime;

    const isAllowTimeTrackingChanged = () => props.entity.settings.allowTimeTracking !== projectSettings.allowTimeTracking;

    const isEstimatedChargeCalculationModeChanged = () => props.entity.settings.estimatedChargeCalculationMode !== projectSettings.estimatedChargeCalculationMode;

    const isUseResourceCalendarChanged = () => props.entity.settings.useResourceCalendar !== projectSettings.useResourceCalendar;

    const isCalendarChanged = () => props.entity.settings.calendar?.id !== projectSettings.calendar?.id;

    const areCalendarValid = () => projectSettings.calendar?.id !== undefined;

    const isTaskTypeChanged = () => props.entity.settings.taskType !== projectSettings.taskType;

    const isEffortDrivenChanged = () => props.entity.settings.effortDriven !== projectSettings.effortDriven;

    const buildUpdates = (): ProjectSettingsUpdateData =>
        getUpdates<IInsightsData & ProjectSettings>({ ...props.entity.insights, ...props.entity.settings }, { ...projectInsights, ...projectSettings });

    const saveWithUpdates = (updates: ProjectSettingsUpdateData) => {
        props.onSave(updates);
        props.onDismiss();
    };

    const onSave = async () => {
        const updates = buildUpdates();

        if (isMilestoneFsBehaviorChanged()) {
            const affectedMilestones = await get<{ id: string; name: string }[]>(`api/project/${props.entity.id}/task/zeroDayMilestonesWithFsPredecessor`);
            const isShiftToKeep = props.entity.settings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay
                && !projectSettings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay;

            setKeepModeAction('manual');
            setPendingBehaviorChange({
                affectedMilestones,
                shouldRecalculateImmediately: !isShiftToKeep
            });
            return;
        }

        saveWithUpdates(updates);
    }

    const isDataValid = areProcessSettingsValid() && areCalendarValid();

    const isDataChanged = 
        isCalculationTypeChanged()
        || isCalculationModeChanged()
        || isMilestoneFsBehaviorChanged()
        || isCalculatedAutomaticalyChanged()
        || isCalculateStartFinishDateChanged()
        || isPrimaryScheduleChanged()
        || isTimeTrackingSettingChanged()
        || isAllowTimeTrackingChanged()
        || areProcessSettingsChanged()
        || isEstimatedChargeCalculationModeChanged()
        || isUseResourceCalendarChanged()
        || isCalendarChanged()
        || isTaskTypeChanged()
        || isEffortDrivenChanged();

    const canSaveChanges = isDataValid && isDataChanged;
    
    const onDismiss = React.useCallback((ev: React.SyntheticEvent<HTMLElement>) => {
        if (canSaveChanges && ev?.currentTarget?.classList?.contains("ms-Overlay")) {
            setShowHasChangesWarning(true);
            ev.preventDefault();
            return;
        }
        props.onDismiss();
    }, [canSaveChanges, props.onDismiss, setShowHasChangesWarning]);

    return <ExpandablePanel
        className="project-settings-panel"
        isOpen
        type={PanelType.custom}
        customWidth={PANEL_CUSTOM_WIDTH}
        isLightDismiss
        onDismiss={onDismiss}
        onRenderHeader={() => <div className="ms-Panel-header">
            <p className="ms-Panel-headerText">Project Settings</p>
            <div className='ms-Panel-secondaryText'>Select project settings for the current project.</div>
        </div>}
        onRenderFooterContent={() => <>
            {showHasChangesWarning && <DirtyFormMessageBar primaryCommandLabel={saveButtonText} cancelCommandLabel={cancelButtonText} />}
            <div className='commands'>
                <PrimaryButton
                    text={saveButtonText}
                    disabled={!canSaveChanges}
                    onClick={onSave} />
                <DefaultButton text={cancelButtonText} onClick={props.onDismiss} />
            </div>
        </>}
    >
        {sectionsSettings.map(section => <Collapsible {...section} >
            {section.onRenderBody()}
        </Collapsible>)}

        {pendingBehaviorChange && <ConfirmationDialog
            onDismiss={() => setPendingBehaviorChange(undefined)}
            onYes={() => {
                const updates = buildUpdates();
                updates.recalculateZeroDayMilestonesWithFsPredecessor = pendingBehaviorChange.shouldRecalculateImmediately
                    ? true
                    : keepModeAction === 'recalculate';
                saveWithUpdates(updates);
            }}
            onNo={() => undefined}
            yesButtonProps={{ text: 'Change' }}
            noButtonProps={{ text: 'Cancel' }}
            dialogContentProps={{
                title: 'Change 0-duration Tasks with FS Predecessors Behavior',
            }}
            modalProps={{ className: 'remove-dialog milestone-fs-behavior-dialog' }}>
            <div className='dialog-intro'>
                Are you sure you want to change the behavior of 0-duration tasks with Finish-to-Start predecessors in PPM Express schedule?
            </div>
            {pendingBehaviorChange.affectedMilestones.length > 0 && <>
                {!pendingBehaviorChange.shouldRecalculateImmediately && <div>
                    <div className='dialog-section-label'>
                        Select what should happen to the following existing 0-duration tasks with Finish-to-Start predecessors:
                    </div>
                    <ChoiceGroup
                        selectedKey={keepModeAction}
                        onChange={(ev, option) => setKeepModeAction((option?.key as 'recalculate' | 'manual') ?? 'manual')}
                        options={[
                            { key: 'recalculate', text: 'Re-calculate the tasks in the PPM Express schedule immediately' },
                            { key: 'manual', text: 'I will adjust the tasks manually later' }
                        ]}
                    />
                </div>}

                <div className='affected-milestones'>
                    <div>
                        {pendingBehaviorChange.shouldRecalculateImmediately
                            ? 'The following tasks in the PPM Express schedule will be re-calculated immediately:'
                            : '0-duration tasks with Finish-to-Start predecessors:'}
                    </div>
                    <ul>
                        {pendingBehaviorChange.affectedMilestones.map(_ => <li key={_.id}>{_.name}</li>)}
                    </ul>
                </div>
            </>}
        </ConfirmationDialog>}
    </ExpandablePanel>;
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        user: state.user,
        tenant: state.tenant,
        tenantInsights: state.tenant.insights,
    };
}

export default connect(mapStateToProps)(ProjectSettingsPanel);

function getUpdates<T>(original: T, changed: T): Partial<T> {
    const result: Partial<T> = {};
    for (const p in changed) {
        if (original[p] !== changed[p]) {
            result[p] = changed[p];
        }
    }
    return result;
}